/**
 * `GeoMap` (D529): a choropleth (areas or grid cells), a bubble map (points) and flow lines over the vendor-free slippy-map
 * arithmetic of `@vello/maps-ui`. No new map provider: when the screen passes a validated TILE SOURCE (configuration, never
 * code), raster tiles are laid behind the data and the source's attribution is printed; otherwise, with a `basemapStyleUrl`
 * (D797), the platform's own vector basemap is drawn behind it (`MapBasemap`, the api's style) with the credit it carries;
 * with neither — and always in tests — the data is drawn over a plain graticule, which is the static fallback and holds the
 * whole chart for the PNG export (an external map would taint the canvas, so the export leaves it out).
 *
 * Areas take the five-step heat scale AND print their value on focus; bubbles are sized by value; a flow's width is its value.
 * Keyboard: Left / Right (Up / Down) step through the features from the strongest down; Enter / Space selects.
 */
import { useRef, useState, type ReactElement } from 'react';
import { buildHeatGrid, buildSeriesSummary, clamp, geoBounds, isFiniteNumber, roundCoord, type GeoBinning, type GeoBounds } from '@vello/charts-core';
import { project, tileSourceIssue, tileUrl, toScreen, unproject, visibleTiles, type TileSource } from '@vello/maps-ui';
import { ChartToolbar, HeatLegend, chartState, emitSelect, heatFill, isChosen, type ChartSelectProps } from './bi-kit';
import type { ChartCsv, ChartExportProps } from './export';
import { markAttrs } from './mark-state';
import { ChartLive, ChartTooltip, cx, defaultFormatValue, joinSummary, seriesColor, useActivePoint, useElementWidth, useMotionState } from './kit';
import type { ChartBaseProps } from './line-chart';
import { MapBasemap } from '../map-basemap';

export interface GeoCoord {
  readonly lat: number;
  readonly lng: number;
}

/** An area given as one or more rings of coordinates (a neighbourhood, a zone, an H3 cell's boundary). */
export interface GeoRegion {
  readonly key: string;
  readonly label: string;
  readonly value: number | null;
  readonly rings: readonly (readonly GeoCoord[])[];
}

/** A rectangular grid cell (the output of `binGeoPoints`). */
export interface GeoCellDatum {
  readonly key: string;
  readonly label: string;
  readonly value: number | null;
  readonly bounds: GeoBounds;
}

export interface GeoPointDatum extends GeoCoord {
  readonly key: string;
  readonly label: string;
  readonly value: number;
}

export interface GeoFlowDatum {
  readonly key: string;
  readonly label: string;
  readonly from: GeoCoord;
  readonly to: GeoCoord;
  readonly value: number;
}

export interface GeoMapProps extends ChartBaseProps, ChartSelectProps, ChartExportProps {
  readonly regions?: readonly GeoRegion[];
  readonly cells?: readonly GeoCellDatum[];
  readonly points?: readonly GeoPointDatum[];
  readonly flows?: readonly GeoFlowDatum[];
  /** Raster tiles behind the data (validated; an invalid source is ignored and the graticule is drawn). */
  readonly tileSource?: TileSource;
  /** D797: the platform basemap's style (`@vello/maps-ui` `basemapStyleUrl`), drawn behind the data when no tile source is given. */
  readonly basemapStyleUrl?: string;
  readonly height?: number;
  /** Heat scale cut: equal parts of the range, or by count of features (default `quantile`: a map is usually skewed). */
  readonly scale?: 'linear' | 'quantile';
  /** What the values are called (localized), used in the tooltip and the summary. */
  readonly valueLabel?: string;
}

/** Turn the output of `binGeoPoints` into cells for the map (the value is the count, or the sum with `useSum`). */
export function geoCellsFromBinning(binning: GeoBinning, label: (cell: GeoBinning['cells'][number]) => string, useSum = false): GeoCellDatum[] {
  return binning.cells.map((c) => ({ key: c.key, label: label(c), value: useSum ? c.sum : c.count, bounds: c.bounds }));
}

interface Feature {
  readonly key: string;
  readonly label: string;
  readonly value: number | null;
  readonly kind: 'area' | 'point' | 'flow';
  readonly coords: readonly GeoCoord[];
  readonly rings?: readonly (readonly GeoCoord[])[];
  readonly to?: GeoCoord;
}

const FALLBACK_WIDTH = 480;
const PAD = 24;

function featuresOf(props: GeoMapProps): Feature[] {
  const out: Feature[] = [];
  for (const r of props.regions ?? []) out.push({ key: r.key, label: r.label, value: r.value, kind: 'area', coords: r.rings.flat(), rings: r.rings });
  for (const c of props.cells ?? []) {
    const b = c.bounds;
    const ring: GeoCoord[] = [{ lat: b.south, lng: b.west }, { lat: b.south, lng: b.east }, { lat: b.north, lng: b.east }, { lat: b.north, lng: b.west }];
    out.push({ key: c.key, label: c.label, value: c.value, kind: 'area', coords: ring, rings: [ring] });
  }
  for (const p of props.points ?? []) out.push({ key: p.key, label: p.label, value: p.value, kind: 'point', coords: [{ lat: p.lat, lng: p.lng }] });
  for (const f of props.flows ?? []) out.push({ key: f.key, label: f.label, value: f.value, kind: 'flow', coords: [f.from, f.to], to: f.to });
  return out.filter((f) => f.coords.every((c) => isFiniteNumber(c.lat) && isFiniteNumber(c.lng) && Math.abs(c.lat) <= 90 && Math.abs(c.lng) <= 180));
}

/** Areas, bubbles and flows on a map of the market, with or without tiles. `onSelect` reports the feature key. */
export function GeoMap(props: GeoMapProps): ReactElement {
  const height = props.height ?? 320;
  return chartState(props, height, featuresOf(props).length > 0) ?? <GeoMapPlot {...props} height={height} />;
}

function GeoMapPlot(props: GeoMapProps & { height: number }): ReactElement {
  const format = props.formatValue ?? defaultFormatValue;
  const features = featuresOf(props);
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);
  const tiles = props.tileSource !== undefined && tileSourceIssue(props.tileSource) === undefined ? props.tileSource : undefined;
  const basemap = tiles === undefined ? props.basemapStyleUrl : undefined;
  const [credit, setCredit] = useState<string | undefined>(undefined);

  // The keyboard walks the features from the strongest down.
  const order = features.map((_, i) => i).sort((a, b) => (features[b]?.value ?? -Infinity) - (features[a]?.value ?? -Infinity) || a - b);
  const active = useActivePoint(order.length, props.onSelect !== undefined ? (i) => emitSelect(props, features[order[i] ?? 0]?.key ?? '', props.dimension ?? 'place') : undefined);

  // View: the zoom that fits every coordinate. With tiles the zoom is a whole number (tiles exist only at whole zooms).
  const all = features.flatMap((f) => f.coords);
  const box = geoBounds(all) as GeoBounds;
  const topLeft = project({ latitude: box.north, longitude: box.west }, 0);
  const bottomRight = project({ latitude: box.south, longitude: box.east }, 0);
  const spanX = Math.max(1e-6, bottomRight.x - topLeft.x);
  const spanY = Math.max(1e-6, bottomRight.y - topLeft.y);
  const innerW = Math.max(40, width - 2 * PAD);
  const innerH = Math.max(40, props.height - 2 * PAD);
  const fitted = Math.log2(Math.min(innerW / spanX, innerH / spanY));
  const single = spanX < 1e-3 && spanY < 1e-3;
  const rawZoom = single ? 12 : clamp(fitted, 0, 18);
  const zoom = tiles !== undefined ? clamp(Math.floor(rawZoom), tiles.minZoom, tiles.maxZoom) : rawZoom;
  const center = unproject({ x: (topLeft.x + bottomRight.x) / 2, y: (topLeft.y + bottomRight.y) / 2 }, 0);
  const view = { center, zoom };
  const viewport = { width, height: props.height };
  const at = (c: GeoCoord): { x: number; y: number } => {
    const p = toScreen({ latitude: c.lat, longitude: c.lng }, view, viewport);
    return { x: roundCoord(p.left), y: roundCoord(p.top) };
  };

  const areas = features.filter((f) => f.kind === 'area');
  const heat = buildHeatGrid({ rows: 1, cols: areas.length, cells: areas.map((f, col) => ({ row: 0, col, value: f.value })), scale: props.scale ?? 'quantile' });
  const areaLevel = new Map(areas.map((f, i) => [f.key, heat.cells[i]?.level ?? null]));
  const maxPoint = Math.max(1e-9, ...features.filter((f) => f.kind === 'point').map((f) => f.value ?? 0));
  const maxFlow = Math.max(1e-9, ...features.filter((f) => f.kind === 'flow').map((f) => f.value ?? 0));

  const a = active.index;
  const activeFeature = a === null ? undefined : features[order[a] ?? 0];
  const centroid = (f: Feature): { x: number; y: number } => {
    const pts = f.coords.map(at);
    return { x: pts.reduce((s, p) => s + p.x, 0) / pts.length, y: pts.reduce((s, p) => s + p.y, 0) / pts.length };
  };

  const ranked = order.map((i) => features[i] as Feature);
  const summary =
    props.ariaLabel ??
    joinSummary([
      props.title,
      buildSeriesSummary(ranked.map((f) => ({ label: f.label, value: f.value })), { format, maxItems: 10 }),
    ]);
  const csv = (): ChartCsv => ({
    header: ['key', 'label', props.valueLabel ?? 'value', 'lat', 'lng'],
    rows: ranked.map((f) => [f.key, f.label, f.value, centroidCoord(f).lat, centroidCoord(f).lng]),
  });

  const tileEls = tiles !== undefined ? visibleTiles(view, viewport) : [];

  return (
    <div className={cx('vello-chart', 'vello-chart--geo', props.className)} data-motion={motion}>
      <ChartToolbar props={props} title={props.title} csv={csv} />
      <div ref={ref} className="vello-chart__plot vello-chart__focus" role="img" aria-label={summary} tabIndex={0} onKeyDown={active.onKeyDown} onFocus={active.onFocus} onBlur={active.onBlur}>
        {basemap !== undefined && <MapBasemap styleUrl={basemap} center={center} zoom={zoom} onAttribution={setCredit} />}
        <svg className="vello-chart__svg vello-geo__svg" data-basemap={basemap !== undefined ? 'true' : undefined} width={width} height={props.height} viewBox={`0 0 ${width} ${props.height}`} onPointerLeave={active.onPointerLeave}>
          {basemap === undefined && <rect className="vello-geo__ground" x={0} y={0} width={width} height={props.height} />}
          {tileEls.length === 0 && basemap === undefined &&
            [1, 2, 3].map((k) => (
              <g key={k} className="vello-geo__graticule">
                <line x1={(width * k) / 4} x2={(width * k) / 4} y1={0} y2={props.height} />
                <line x1={0} x2={width} y1={(props.height * k) / 4} y2={(props.height * k) / 4} />
              </g>
            ))}
          {tileEls.map((t) => (
            <image key={t.key} className="vello-geo__tile" href={tileUrl((tiles as TileSource).urlTemplate, t.z, t.x, t.y)} x={roundCoord(t.left)} y={roundCoord(t.top)} width={256} height={256} preserveAspectRatio="none" />
          ))}
          {features
            .filter((f) => f.kind === 'area')
            .map((f, i) => {
              const d = (f.rings ?? []).map((ring) => ring.map((c, k) => `${k === 0 ? 'M' : 'L'}${at(c).x} ${at(c).y}`).join('') + 'Z').join('');
              const isActive = activeFeature?.key === f.key;
              return (
                <path
                  key={f.key}
                  className="vello-geo__area vello-chart__fade"
                  d={d}
                  fill={heatFill(areaLevel.get(f.key) ?? null)}
                  data-empty={f.value === null ? 'true' : undefined}
                  {...markAttrs(props, f.key, isActive)}
                  onPointerEnter={() => active.set(order.findIndex((o) => features[o]?.key === f.key))}
                  onPointerLeave={active.onPointerLeave}
                  onClick={() => emitSelect(props, f.key, props.dimension ?? 'place')}
                  style={{ ['--i' as string]: Math.min(i, 10) }}
                />
              );
            })}
          {features
            .filter((f) => f.kind === 'flow')
            .map((f) => {
              const from = at(f.coords[0] as GeoCoord);
              const to = at(f.coords[1] as GeoCoord);
              const bend = Math.hypot(to.x - from.x, to.y - from.y) * 0.15;
              const mx = (from.x + to.x) / 2 - ((to.y - from.y) / (Math.hypot(to.x - from.x, to.y - from.y) || 1)) * bend;
              const my = (from.y + to.y) / 2 + ((to.x - from.x) / (Math.hypot(to.x - from.x, to.y - from.y) || 1)) * bend;
              return (
                <g
                  key={f.key}
                  {...markAttrs(props, f.key, activeFeature?.key === f.key)}
                  onPointerEnter={() => active.set(order.findIndex((o) => features[o]?.key === f.key))}
                  onPointerLeave={active.onPointerLeave}
                  onClick={() => emitSelect(props, f.key, props.dimension ?? 'place')}
                >
                  {(activeFeature?.key === f.key || isChosen(props, f.key)) && (
                    <path className="vello-geo__flow-halo" data-chosen={isChosen(props, f.key) ? 'true' : undefined} d={`M${from.x} ${from.y}Q${roundCoord(mx)} ${roundCoord(my)} ${to.x} ${to.y}`} strokeWidth={5 + 4 * ((f.value ?? 0) / maxFlow)} />
                  )}
                  <path className="vello-geo__flow vello-chart__fade" d={`M${from.x} ${from.y}Q${roundCoord(mx)} ${roundCoord(my)} ${to.x} ${to.y}`} stroke={seriesColor(1)} strokeWidth={1.5 + 4 * ((f.value ?? 0) / maxFlow)} />
                  <circle className="vello-chart__dot" cx={to.x} cy={to.y} r={3.5} fill={seriesColor(1)} />
                </g>
              );
            })}
          {features
            .filter((f) => f.kind === 'point')
            .map((f) => {
              const p = at(f.coords[0] as GeoCoord);
              const r = 4 + 14 * Math.sqrt(Math.max(0, f.value ?? 0) / maxPoint);
              return (
                <circle
                  key={f.key}
                  className="vello-geo__point vello-chart__pop"
                  cx={p.x}
                  cy={p.y}
                  r={roundCoord(r)}
                  fill={seriesColor(0)}
                  {...markAttrs(props, f.key, activeFeature?.key === f.key)}
                  onPointerEnter={() => active.set(order.findIndex((o) => features[o]?.key === f.key))}
                  onPointerLeave={active.onPointerLeave}
                  onClick={() => emitSelect(props, f.key, props.dimension ?? 'place')}
                />
              );
            })}
        </svg>
        {activeFeature !== undefined && (
          <ChartTooltip
            x={centroid(activeFeature).x}
            y={centroid(activeFeature).y}
            width={width}
            heading={activeFeature.label}
            rows={[{ key: 'v', ...(props.valueLabel !== undefined ? { label: props.valueLabel } : {}), value: activeFeature.value === null ? '–' : format(activeFeature.value) }]}
          />
        )}
      </div>
      <ChartLive text={activeFeature === undefined ? '' : `${activeFeature.label}: ${activeFeature.value === null ? '–' : format(activeFeature.value)}`} />
      {areas.length > 0 && heat.domain !== null && <HeatLegend min={format(heat.domain[0])} max={format(heat.domain[1])} />}
      {tiles !== undefined && <p className="vello-geo__attribution">{tiles.attribution}</p>}
      {basemap !== undefined && credit !== undefined && <p className="vello-geo__attribution">{credit}</p>}
    </div>
  );
}

function centroidCoord(f: Feature): GeoCoord {
  const n = Math.max(1, f.coords.length);
  return { lat: roundCoord(f.coords.reduce((s, c) => s + c.lat, 0) / n, 6), lng: roundCoord(f.coords.reduce((s, c) => s + c.lng, 0) / n, 6) };
}
